import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * @interface UserContextState
 * @description Define la forma del estado de contexto del usuario gestionado por Zustand.
 */
interface UserContextState {
  /**
   * @property {number} idCorporativo - El ID del corporativo del usuario, o 0 si no hay corporativo.
   */
  IdCorporativo: number;

  /**
   * @property {number} idEmpresa - El ID de la empresa del usuario, o 0 si no hay empresa.
   */
  IdEmpresa: number;

  /**
   * @function setCorporativo
   * @description Establece el ID del corporativo del usuario.
   * @param {number} idCorporativo - El ID del corporativo a establecer.
   */
  setCorporativo: (IdCorporativo: number) => void;

  /**
   * @function setEmpresa
   * @description Establece el ID de la empresa del usuario.
   * @param {number} idEmpresa - El ID de la empresa a establecer.
   */
  setEmpresa: (IdEmpresa: number) => void;

  /**
   * @function clearCorporativo
   * @description Limpia el ID del corporativo del usuario.
   */
  clearCorporativo: () => void;

  /**
   * @function clearEmpresa
   * @description Limpia el ID de la empresa del usuario.
   */
  clearEmpresa: () => void;

  /**
   * @function clearAll
   * @description Limpia todos los IDs del contexto del usuario.
   */
  clearAll: () => void;
}

/**
 * @constant useUserContextStore
 * @description Un almacén de Zustand para gestionar el estado del contexto del usuario (IdCorporativo e IdEmpresa).
 * El almacén se persiste en localStorage para mantener el estado entre sesiones del navegador.
 * @type {import('zustand').UseBoundStore<import('zustand').StoreApi<UserContextState>>}
 */
export const useUserContextStore = create<UserContextState>()(
  persist(
    (set) => ({
      IdCorporativo: 0,
      IdEmpresa: 0,

      setCorporativo: (IdCorporativo: number) => {
        set({ IdCorporativo });
        //console.log('🔄 User Context Store - Corporativo establecido:', IdCorporativo);
      },

      setEmpresa: (IdEmpresa: number) => {
        set({ IdEmpresa });
        //console.log('🔄 User Context Store - Empresa establecida:', IdEmpresa);
      },

      clearCorporativo: () => {
        set({ IdCorporativo: 0 });
        //console.log('🔄 User Context Store - Corporativo limpiado');
      },

      clearEmpresa: () => {
        set({ IdEmpresa: 0 });
       // console.log('🔄 User Context Store - Empresa limpiada');
      },

      clearAll: () => {
        set({ IdCorporativo: 0, IdEmpresa: 0 });
        //console.log('🔄 User Context Store - Todo limpiado');
      },
    }),
    {
      name: 'user-context-storage',
      partialize: (state) => ({
        IdCorporativo: state.IdCorporativo,
        IdEmpresa: state.IdEmpresa,
      }),
    }
  )
);

